import { useMemo } from 'react';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import {
  formatPlanDate,
  formatPlanDateInZone,
  formatPlanDuration,
  getSubscriptionState,
  planEndDate,
  todayInTimeZone,
} from '../utils/subscriptionDateUtil';
import { POS_SUBSCRIPTION_STATUS, POS_TIMEZONES } from '../constants/posConst';
import PosSubscriptionStatusPill from './PosSubscriptionStatusPill';
import '../css/PosAdmin.css';
import '../css/PosSubscription.css';

/** The "no subscription" choice. */
const NO_PLAN = '';

/**
 * The Subscriptions tab of client registration: which plan this client is on,
 * from when to when, and the timezone those dates are counted in.
 *
 * CONTROLLED. The form owns the choice and saves it with everything else, so
 * nothing is written until Save — the same as Staff and Roles.
 *
 * Picking a plan fills the dates in (start today, end from the plan's length),
 * but both stay editable: an administrator extending a club by a week as a
 * courtesy should not have to invent a plan for it.
 *
 * @param {object} props
 * @param {object[]} props.plans - every plan, trials included
 * @param {boolean} props.plansLoading
 * @param {object|null} props.existing - the subscription stored now
 * @param {boolean} props.loading - the stored subscription is still loading
 * @param {Error|null} props.loadError - it could not be read; the tab then
 *   refuses to edit, so a failed read is never saved back as "no plan"
 * @param {{planId: string, startDate: string, endDate: string}} props.value
 * @param {(value: {planId: string, startDate: string, endDate: string}) => void} props.onChange
 * @param {string} props.timezone
 * @param {(timezone: string) => void} props.onTimezoneChange
 * @param {object} props.config - for the currency symbol
 * @param {boolean} [props.disabled]
 * @returns {JSX.Element}
 */
function PosClientSubscriptionTab({
  plans,
  plansLoading,
  existing,
  loading,
  loadError,
  value,
  onChange,
  timezone,
  onTimezoneChange,
  config,
  disabled = false,
}) {
  const today = todayInTimeZone(timezone);
  const state = useMemo(() => getSubscriptionState(existing, timezone), [existing, timezone]);

  // A stored plan that has since been deleted still has to be selectable, or
  // simply opening this tab would look like the client has no plan.
  const options = useMemo(() => {
    if (!existing?.planId || plans.some((plan) => plan.id === existing.planId)) return plans;
    return [...plans, {
      id: existing.planId,
      name: `${existing.planName} (no longer offered)`,
      price: existing.price,
      durationValue: existing.durationValue,
      durationUnit: existing.durationUnit,
      isTrial: existing.isTrial,
    }];
  }, [plans, existing]);

  /**
   * Chooses a plan and fills the dates from it.
   *
   * @param {string} planId
   * @returns {void}
   */
  const handlePlan = (planId) => {
    if (planId === NO_PLAN) {
      onChange({ planId: NO_PLAN, startDate: '', endDate: '' });
      return;
    }
    const plan = options.find((option) => option.id === planId);
    const startDate = value.startDate || today;
    onChange({ planId, startDate, endDate: planEndDate(startDate, plan.durationValue, plan.durationUnit) });
  };

  /**
   * Moves the start date, and the end date with it.
   *
   * @param {string} startDate
   * @returns {void}
   */
  const handleStart = (startDate) => {
    const plan = options.find((option) => option.id === value.planId);
    onChange({
      ...value,
      startDate,
      endDate: plan && startDate ? planEndDate(startDate, plan.durationValue, plan.durationUnit) : value.endDate,
    });
  };

  if (loading || plansLoading) {
    return <div className="pa-loading pa-loading-inline">{spinnerRing('Loading subscription')}</div>;
  }

  return (
    <div className="pa-form-grid">
      <div className="pa-field pa-field-wide">
        <span className="pa-field-label">Current subscription</span>
        {loadError ? (
          <p className="pa-field-hint">
            This client&rsquo;s subscription could not be read: {loadError.message}. It is being
            left exactly as it is — saving this form will not change it.
          </p>
        ) : existing ? (
          <div className="ps-current">
            <div className="ps-current-main">
              <strong>{existing.planName}</strong>
              <PosSubscriptionStatusPill status={state.status} daysLeft={state.daysLeft} />
            </div>
            <span className="pa-field-hint">
              {formatPlanDate(existing.startDate)} – {formatPlanDateInZone(existing.endDate, timezone)}
              {existing.extraStaff ? ` · ${existing.extraStaff} extra staff account${existing.extraStaff === 1 ? '' : 's'}` : ''}
              {existing.source === 'application' ? ' · activated from a client application' : ''}
            </span>
          </div>
        ) : (
          <p className="pa-field-hint">None yet. Pick a plan below — a free trial is a good start for a new client.</p>
        )}
      </div>

      <label className="pa-field">
        <span className="pa-field-label">Timezone</span>
        <select className="pa-input" value={timezone} onChange={(event) => onTimezoneChange(event.target.value)} disabled={disabled}>
          {!POS_TIMEZONES.some((zone) => zone.key === timezone) && <option value={timezone}>{timezone}</option>}
          {POS_TIMEZONES.map((zone) => (
            <option key={zone.key} value={zone.key}>{zone.label}</option>
          ))}
        </select>
        <span className="pa-field-hint">
          Start and end dates are counted in this timezone — a plan ending on the 30th ends at
          midnight on the 30th where the club is. Today there is {formatPlanDateInZone(today, timezone)}.
        </span>
      </label>

      {!loadError && (
        <>
          <div className="pa-field pa-field-wide">
            <span className="pa-field-label">Availed plan</span>
            <div className="pa-choices" role="radiogroup" aria-label="Availed plan">
              <label className={`pa-choice${value.planId === NO_PLAN ? ' active' : ''}`}>
                <input type="radio" name="clientPlan" checked={value.planId === NO_PLAN} onChange={() => handlePlan(NO_PLAN)} disabled={disabled} />
                <span className="pa-choice-text">
                  <span className="pa-choice-label">No subscription</span>
                  <span className="pa-choice-desc">No banner is shown at the till.</span>
                </span>
              </label>
              {options.map((plan) => (
                <label key={plan.id} className={`pa-choice${value.planId === plan.id ? ' active' : ''}`}>
                  <input type="radio" name="clientPlan" checked={value.planId === plan.id} onChange={() => handlePlan(plan.id)} disabled={disabled} />
                  <span className="pa-choice-text">
                    <span className="pa-choice-label">{plan.name}</span>
                    <span className="pa-choice-desc">
                      {plan.isTrial || !plan.price ? 'Free' : formatMoney(plan.price, config)}
                      {' · '}
                      {formatPlanDuration(plan.durationValue, plan.durationUnit)}
                      {plan.isTrial ? ' · free trial' : ''}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            {options.length === 0 && (
              <span className="pa-field-hint">No plans exist yet. Add them under Subscription Plans in the menu.</span>
            )}
          </div>

          {value.planId !== NO_PLAN && (
            <>
              <label className="pa-field">
                <span className="pa-field-label">Start date</span>
                <input
                  className="pa-input"
                  type="date"
                  value={value.startDate}
                  onChange={(event) => handleStart(event.target.value)}
                  disabled={disabled}
                />
              </label>
              <label className="pa-field">
                <span className="pa-field-label">End date</span>
                <input
                  className="pa-input"
                  type="date"
                  value={value.endDate}
                  min={value.startDate || undefined}
                  onChange={(event) => onChange({ ...value, endDate: event.target.value })}
                  disabled={disabled}
                />
                <span className="pa-field-hint">
                  Filled in from the plan&rsquo;s length and inclusive — the client can use the till
                  through the end of this day. Change it to extend or shorten this period.
                </span>
              </label>
            </>
          )}

          {state.status === POS_SUBSCRIPTION_STATUS.EXPIRED && (
            <p className="pa-field-hint pa-field-wide">
              Expired subscriptions only show a red banner at the till — the till keeps working.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default PosClientSubscriptionTab;
